import { useState, useEffect } from "react";
import { Row, Col, Form, Button, Tabs, message, Spin } from "antd";
import { useSelector } from "react-redux";
import { Get, Post } from "../../config/api";
import { TERMS, PRIVACY_POLICY } from "../../config/constants/api";
import { FaFileContract, FaShieldAlt } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const TermsAndConditions = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("terms");
  const [form] = Form.useForm();
  const token = useSelector((state) => state.user.userToken);

  // Load current content on mount
  useEffect(() => {
    loadContent();
    // eslint-disable-next-line
  }, [activeTab]);

  const loadContent = async () => {
    try {
      setInitialLoading(true);
      const endpoint =
        activeTab === "terms"
          ? TERMS.getTerms
          : PRIVACY_POLICY.getPrivacyPolicy;

      // Both endpoints are public, no token needed
      const response = await Get(endpoint, null);
      if (response?.data) {
        const fieldName = activeTab === "terms" ? "termsAndConditions" : "privacyPolicy";
        // Handle different response structures
        form.setFieldsValue({
          [fieldName]: response.data.content || response.data.text || response.data[fieldName] || response.data || "",
        });
      } else {
        form.setFieldsValue({
          [activeTab === "terms" ? "termsAndConditions" : "privacyPolicy"]: "",
        });
      }
    } catch (err) {
      console.error("Failed to load content:", err);
      message.error("Failed to load content.");
      form.setFieldsValue({
        [activeTab === "terms" ? "termsAndConditions" : "privacyPolicy"]: "",
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const endpoint =
        activeTab === "terms"
          ? TERMS.setTerms
          : PRIVACY_POLICY.setPrivacyPolicy;

      const fieldName = activeTab === "terms" ? "termsAndConditions" : "privacyPolicy";
      // Send the HTML content from the editor
      const payload = {
        content: values[fieldName] || "",
      };

      const response = await Post(endpoint, payload, token);
      if (response?.success || response?.status) {
        message.success(
          activeTab === "terms"
            ? "Terms and Conditions updated successfully!"
            : "Privacy Policy updated successfully!"
        );
      } else {
        message.error(response?.message || "Failed to update content.");
      }
    } catch (err) {
      console.error("Failed to update content:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update content. Please try again.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    form.resetFields();
  };

  const tabItems = [
    {
      key: "terms",
      label: (
        <span className="flex items-center gap-2">
          <FaFileContract />
          Terms and Conditions
        </span>
      ),
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-6"
        >
          <Form.Item
            name="termsAndConditions"
            label={
              <p className="font-euclid text-sm text-[#34324B] font-bold">
                Terms and Conditions Content
              </p>
            }
            rules={[
              {
                required: true,
                message: "Please enter terms and conditions content!",
              },
            ]}
            getValueFromEvent={(value) => value}
          >
            <ReactQuill
              theme="snow"
              placeholder="Enter terms and conditions content here..."
              style={{
                height: "400px",
                marginBottom: "50px",
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  [{ font: [] }],
                  [{ size: [] }],
                  ["bold", "italic", "underline", "strike", "blockquote"],
                  [
                    { list: "ordered" },
                    { list: "bullet" },
                    { indent: "-1" },
                    { indent: "+1" },
                  ],
                  ["link", "image", "video"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  ["clean"],
                ],
              }}
              formats={[
                "header",
                "font",
                "size",
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "list",
                "bullet",
                "indent",
                "link",
                "image",
                "video",
                "color",
                "background",
                "align",
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="bg-button text-black border-none font-bold font-euclid hover:bg-button"
              size="large"
            >
              Update Terms and Conditions
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "privacy",
      label: (
        <span className="flex items-center gap-2">
          <FaShieldAlt />
          Privacy Policy
        </span>
      ),
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-6"
        >
          <Form.Item
            name="privacyPolicy"
            label={
              <p className="font-euclid text-sm text-[#34324B] font-bold">
                Privacy Policy Content
              </p>
            }
            rules={[
              {
                required: true,
                message: "Please enter privacy policy content!",
              },
            ]}
            getValueFromEvent={(value) => value}
          >
            <ReactQuill
              theme="snow"
              placeholder="Enter privacy policy content here..."
              style={{
                height: "400px",
                marginBottom: "50px",
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  [{ font: [] }],
                  [{ size: [] }],
                  ["bold", "italic", "underline", "strike", "blockquote"],
                  [
                    { list: "ordered" },
                    { list: "bullet" },
                    { indent: "-1" },
                    { indent: "+1" },
                  ],
                  ["link", "image", "video"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  ["clean"],
                ],
              }}
              formats={[
                "header",
                "font",
                "size",
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "list",
                "bullet",
                "indent",
                "link",
                "image",
                "video",
                "color",
                "background",
                "align",
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="bg-button text-black border-none font-bold font-euclid hover:bg-button"
              size="large"
            >
              Update Privacy Policy
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Row justify="center" className="py-8">
      <Col md={23} xs={22}>
        <div
          className="bg-white rounded-lg sm:py-16 py-8 my-12"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <div className="mx-4 lg:mx-8">
            <h3 className="font-tavi lg:text-3xl text-2xl max-sm:text-lg font-normal text-black mb-6">
              Terms and Conditions Management
            </h3>

            {initialLoading ? (
              <div className="flex justify-center items-center py-20">
                <Spin size="large" />
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                items={tabItems}
                onChange={handleTabChange}
                className="terms-tabs"
              />
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default TermsAndConditions;

